import React from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { TrendingUp, ClipboardList, AlertTriangle, CheckCircle2 } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
} from "recharts";
import type {
  TrendDataPoint,
  DefectTrendDataPoint,
  PieChartDataPoint,
  DashboardStats,
} from "@/hooks/useDashboardQuery";

const tooltipStyle = {
  backgroundColor: "hsl(var(--card))",
  border: "1px solid hsl(var(--border))",
  borderRadius: "8px",
};

// --- Inspection Charts ---

interface InspectionChartsProps {
  inspectionTrend: TrendDataPoint[];
  inspectionsByStatus: PieChartDataPoint[];
  inspectionsSimple: PieChartDataPoint[];
  stats: DashboardStats | null;
  showDetailedView: boolean;
  onToggleDetailedView: () => void;
}

export const InspectionCharts = React.memo(function InspectionCharts({
  inspectionTrend,
  inspectionsByStatus,
  inspectionsSimple,
  stats,
  showDetailedView,
  onToggleDetailedView,
}: InspectionChartsProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const pieData = showDetailedView ? inspectionsByStatus : inspectionsSimple;

  return (
    <div data-tour="dashboard-charts" className="grid grid-cols-1 container-grid-2 gap-6">
      {/* Inspection Trend */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <TrendingUp className="h-5 w-5 text-primary" />
            {t("dashboard.inspectionTrend")}
          </CardTitle>
          <CardDescription>{t("dashboard.createdAndCompleted")}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={inspectionTrend}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                <XAxis dataKey="date" tick={{ fontSize: 12 }} className="text-muted-foreground" />
                <YAxis
                  tick={{ fontSize: 12 }}
                  className="text-muted-foreground"
                  allowDecimals={false}
                />
                <Tooltip
                  contentStyle={tooltipStyle}
                  labelStyle={{ color: "hsl(var(--foreground))" }}
                />
                <Bar
                  dataKey="created"
                  name={t("dashboard.created")}
                  fill="hsl(var(--chart-1))"
                  radius={[4, 4, 0, 0]}
                />
                <Bar
                  dataKey="completed"
                  name={t("dashboard.completed")}
                  fill="hsl(var(--chart-2))"
                  radius={[4, 4, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      {/* Inspections by Status Pie Chart */}
      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-lg flex items-center gap-2">
                <ClipboardList className="h-5 w-5 text-primary" />
                {t("dashboard.inspections")} {t("dashboard.byStatus")}
              </CardTitle>
              <CardDescription>{t("dashboard.distribution")}</CardDescription>
            </div>
            <Button variant="ghost" size="sm" onClick={onToggleDetailedView} className="text-xs">
              {showDetailedView ? t("dashboard.simple") : t("dashboard.detailed")}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="h-64">
            {stats && stats.totalInspections > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={pieData}
                    cx="50%"
                    cy="50%"
                    innerRadius={50}
                    outerRadius={80}
                    paddingAngle={2}
                    dataKey="value"
                    label={({ name, value }) => (value > 0 ? `${name}: ${value}` : "")}
                    labelLine={false}
                    style={{ cursor: "pointer" }}
                    onClick={(data) => {
                      if (data?.payload?.route) navigate(data.payload.route);
                    }}
                  >
                    {pieData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip contentStyle={tooltipStyle} />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-full flex items-center justify-center text-muted-foreground">
                <div className="text-center">
                  <ClipboardList className="h-12 w-12 mx-auto mb-2 text-muted-foreground" />
                  <p>{t("dashboard.noInspectionsInPeriod")}</p>
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
});

// --- Defect Charts ---

interface DefectChartsProps {
  defectTrend: DefectTrendDataPoint[];
  defectsByStatus: PieChartDataPoint[];
  defectsSimple: PieChartDataPoint[];
  defectsBySeverity: PieChartDataPoint[];
  stats: DashboardStats | null;
  showDetailedView: boolean;
  onToggleDetailedView: () => void;
  buildDefectsRoute: (status?: string, severity?: string) => string;
}

export const DefectCharts = React.memo(function DefectCharts({
  defectTrend,
  defectsByStatus,
  defectsSimple,
  defectsBySeverity,
  stats,
  showDetailedView,
  onToggleDetailedView,
  buildDefectsRoute,
}: DefectChartsProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const pieData = showDetailedView ? defectsByStatus : defectsSimple;

  return (
    <>
      {/* Defect Trend + Status */}
      <div className="grid grid-cols-1 container-grid-2 gap-6" data-tour="dashboard-defect-charts">
        {/* Defect Trend */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-warning" />
              {t("dashboard.defectTrend")}
            </CardTitle>
            <CardDescription>{t("dashboard.createdAndResolved")}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={defectTrend}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                  <XAxis dataKey="date" tick={{ fontSize: 12 }} className="text-muted-foreground" />
                  <YAxis
                    tick={{ fontSize: 12 }}
                    className="text-muted-foreground"
                    allowDecimals={false}
                  />
                  <Tooltip
                    contentStyle={tooltipStyle}
                    labelStyle={{ color: "hsl(var(--foreground))" }}
                  />
                  <Bar
                    dataKey="created"
                    name={t("dashboard.created")}
                    fill="hsl(var(--chart-1))"
                    radius={[4, 4, 0, 0]}
                  />
                  <Bar
                    dataKey="resolved"
                    name={t("dashboard.resolved")}
                    fill="hsl(var(--chart-2))"
                    radius={[4, 4, 0, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Defects by Status Pie Chart */}
        <Card>
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-lg flex items-center gap-2">
                  <AlertTriangle className="h-5 w-5 text-warning" />
                  {t("dashboard.defects")} {t("dashboard.byStatus")}
                </CardTitle>
                <CardDescription>{t("dashboard.distribution")}</CardDescription>
              </div>
              <Button variant="ghost" size="sm" onClick={onToggleDetailedView} className="text-xs">
                {showDetailedView ? t("dashboard.simple") : t("dashboard.detailed")}
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              {stats && stats.totalDefects > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={pieData}
                      cx="50%"
                      cy="50%"
                      innerRadius={50}
                      outerRadius={80}
                      paddingAngle={2}
                      dataKey="value"
                      label={({ name, value }) => (value > 0 ? `${name}: ${value}` : "")}
                      labelLine={false}
                      style={{ cursor: "pointer" }}
                      onClick={(data) => {
                        if (data?.payload?.route) navigate(data.payload.route);
                      }}
                    >
                      {pieData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip contentStyle={tooltipStyle} />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-full flex items-center justify-center text-muted-foreground">
                  <div className="text-center">
                    <CheckCircle2 className="h-12 w-12 mx-auto mb-2 text-success" />
                    <p>{t("dashboard.noDefectsInPeriod")}</p>
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Defects by Severity */}
      <div className="grid grid-cols-1 container-grid-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-warning" />
              {t("dashboard.defects")} {t("dashboard.bySeverity")}
            </CardTitle>
            <CardDescription>{t("dashboard.priorityDistribution")}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              {stats && stats.totalDefects > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={defectsBySeverity}
                      cx="50%"
                      cy="50%"
                      innerRadius={50}
                      outerRadius={80}
                      paddingAngle={2}
                      dataKey="value"
                      label={({ name, value }) => (value > 0 ? `${name}: ${value}` : "")}
                      labelLine={false}
                      style={{ cursor: "pointer" }}
                      onClick={(data) => {
                        if (data?.payload?.severity)
                          navigate(buildDefectsRoute(undefined, data.payload.severity));
                      }}
                    >
                      {defectsBySeverity.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip contentStyle={tooltipStyle} />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-full flex items-center justify-center text-muted-foreground">
                  <div className="text-center">
                    <CheckCircle2 className="h-12 w-12 mx-auto mb-2 text-success" />
                    <p>{t("dashboard.noDefectsInPeriod")}</p>
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </>
  );
});
